import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import { type settingsScreen_Query } from "@relay/settingsScreen_Query.graphql";
import { type settingsScreen_SetGlobalDiscountCodeMutation } from "@relay/settingsScreen_SetGlobalDiscountCodeMutation.graphql";
import { selectActiveFeatureToggles } from "@store/slices/FeatureTogglesSlice";
import { QUERY, SET_GLOBAL_DISCOUNT_CODE_MUTATION } from "./settings.graphql";
import {
	activeGlobalDiscountCodeClass,
	activeGlobalDiscountWrapperClass,
	globalDiscountStartDateFieldClass,
	globalDiscountStatusBadgeClass,
	headerClass,
	wrapperClass,
} from "./settings.styles";
import { isGlobalDiscountStartDateRejected } from "./settings.utils";
import { BaseScreen } from "../../components/base-screen";
import { Button } from "../../components/button";
import { NoAccess } from "../../components/no-access";
import { SelectDiscountCodeField } from "../../features/discount-codes/select-discount-code-field";
import { type DiscountCode } from "../../features/discount-codes/select-discount-code-field/select-discount-code-field.interface";
import { useHasPermissions } from "../../hooks/use-has-permissions";
import { P2Span } from "../../themes/font-tags";
import { ConfigDatePicker } from "../educational-offer/parts/ConfigDatePicker";

export const SettingsScreen = () => {
	const { t } = useTranslation("common");
	const query = useLazyLoadQuery<settingsScreen_Query>(QUERY, {});

	const [globalDiscountCode, setGlobalDiscountCode] = useState<DiscountCode | undefined>(
		query.Admin.Settings.Settings?.globalDiscountCode as DiscountCode,
	);

	const [startDate, setStartDate] = useState<string | undefined>(
		query.Admin.Settings.Settings?.globalDiscountStartDate ?? undefined,
	);

	const lastSavedStartDateRef = useRef(startDate);

	const isRoot = useHasPermissions("onlyOwnerOfRoot");

	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);
	const hasScheduledGlobalDiscount = activeFeatureToggles?.includes("scheduledGlobalDiscount");

	const [dispatchSetGlobalDiscountCodeId] =
		useMutation<settingsScreen_SetGlobalDiscountCodeMutation>(
			SET_GLOBAL_DISCOUNT_CODE_MUTATION,
		);

	const handleSubmit = () => {
		const effectiveStartDate = hasScheduledGlobalDiscount
			? startDate
			: lastSavedStartDateRef.current;

		if (isGlobalDiscountStartDateRejected(effectiveStartDate, lastSavedStartDateRef.current)) {
			toast.error(t("settings.globalDiscountStartDateInPastError"));
			return;
		}

		dispatchSetGlobalDiscountCodeId({
			variables: {
				input: {
					discountCodeId: globalDiscountCode?.id,
					startDate: effectiveStartDate ?? null,
				},
			},
			onCompleted: () => {
				toast.info(t("settings.globalDiscountSaved"));
				lastSavedStartDateRef.current = effectiveStartDate;
			},
			onError: () => {
				toast.error(t("settings.globalDiscountSaveError"));
			},
		});
	};

	const handleOnChange = (discountCode?: DiscountCode) => {
		setGlobalDiscountCode(discountCode);
	};

	if (!isRoot) return <NoAccess />;

	const activeGlobalDiscount = query.Admin.Settings.Settings?.globalDiscountCode;
	const globalDiscountStatus = query.Admin.Settings.Settings?.globalDiscountStatus;

	const globalDiscountHeading =
		globalDiscountStatus === "SCHEDULED"
			? t("settings.scheduledGlobalDiscount")
			: t("settings.activeGlobalDiscount");

	return (
		<BaseScreen title={t("settings.title")}>
			<div className={wrapperClass}>
				<div className={headerClass}>
					<label htmlFor="value">{t("settings.globalDiscount")}</label>
					{activeGlobalDiscount && (
						<div className={activeGlobalDiscountWrapperClass}>
							<P2Span className={activeGlobalDiscountCodeClass}>
								{globalDiscountHeading}
								<code>
									<pre>{activeGlobalDiscount.code}</pre>
								</code>
								{globalDiscountStatus && globalDiscountStatus !== "NONE" && (
									<span
										className={globalDiscountStatusBadgeClass({
											status: globalDiscountStatus,
										})}
									>
										{t(`settings.globalDiscountStatus.${globalDiscountStatus}`)}
									</span>
								)}
							</P2Span>
						</div>
					)}
				</div>
				<SelectDiscountCodeField
					fieldValue={globalDiscountCode}
					updateField={handleOnChange}
					filterByDiscountTypeKind="System"
					fieldName={"discount-code"}
					required={false}
					isValid={true}
					disabled={false}
				/>

				{hasScheduledGlobalDiscount && (
					<div className={globalDiscountStartDateFieldClass}>
						<label htmlFor="global-discount-start-date">
							{t("settings.globalDiscountStartDate")}
						</label>
						<ConfigDatePicker
							name="global-discount-start-date"
							value={startDate}
							onUpdate={setStartDate}
							disabled={false}
						/>
					</div>
				)}

				<Button
					disabled={false}
					type={ButtonType.Button}
					onClick={handleSubmit}
					icon={EpicIcons.SAVE}
					label={t("settings.saveGlobalDiscount")}
				/>
			</div>
		</BaseScreen>
	);
};
